import { BaseApiService } from './BaseApiService';
import { sharedTokenRefresh } from './tokenRefresh';

export interface CeCatalogVideo {
    continuingEducationVideoId?: string;
    title?: string | null;
    description?: string | null;
    durationSeconds?: number | null;
    priceCents?: number | null;
    hasAccess?: boolean;
    /** When access was recorded (purchase or grant); ISO-8601 from API. */
    purchasedAt?: string | null;
    isPlayable?: boolean;
    /** Signed Stream thumbnail URL when video is ready (short-lived). */
    thumbnailUrl?: string | null;
}

export interface CePlaybackResponse {
    success?: boolean;
    token?: string | null;
    iframeSrc?: string | null;
    hlsManifestUrl?: string | null;
    expiresAt?: string | null;
    message?: string | null;
}

export interface CeAdminVideo {
    continuingEducationVideoId?: string;
    title?: string | null;
    description?: string | null;
    durationSeconds?: number | null;
    priceCents?: number | null;
    cloudflareStreamVideoUid?: string | null;
    streamStatus?: string | null;
    isPublished?: boolean | null;
    sortOrder?: number | null;
    thumbnailUrl?: string | null;
    createdAt?: string | null;
    publishedAt?: string | null;
    viewCount?: number | null;
    grantCount?: number | null;
}

export interface CeVideoViewStat {
    userId?: string;
    userFirstName?: string | null;
    userLastName?: string | null;
    userEmail?: string | null;
    viewCount: number;
    lastViewedAt?: string | null;
}

export class ContinuingEducationApiService extends BaseApiService {
    constructor() {
        super(sharedTokenRefresh);
    }

    async listCatalog(): Promise<{ success?: boolean; videos?: CeCatalogVideo[]; message?: string }> {
        const response = await this.postJson('/v1/continuing-education/catalog/list', {});
        return await this.handleResponse(response);
    }

    async createPurchaseIntent(body: { teamId: string; continuingEducationVideoId: string }): Promise<{
        success?: boolean;
        clientSecret?: string;
        paymentIntentId?: string;
        teamPaymentId?: string;
        teamId?: string;
        message?: string;
    }> {
        const response = await this.postJson('/v1/continuing-education/purchase/create-intent', body);
        return await this.handleResponse(response);
    }

    async confirmPurchase(body: {
        teamId: string;
        continuingEducationVideoId: string;
        paymentMethodId: string;
        paymentMethodType: string;
    }): Promise<{
        success?: boolean;
        /** 'requires_action' when the bank wants 3D Secure: run the challenge, then finalizePurchase. */
        outcome?: string;
        clientSecret?: string;
        paymentIntentId?: string;
        status?: string;
        chargeId?: string;
        teamPaymentId?: string;
        message?: string;
    }> {
        const response = await this.postJson('/v1/continuing-education/purchase/confirm', body);
        return await this.handleResponse(response);
    }

    async finalizePurchase(body: { paymentIntentId: string; paymentMethodType?: string }): Promise<{ success?: boolean; status?: string; message?: string }> {
        const response = await this.postJson('/v1/continuing-education/purchase/finalize', body);
        return await this.handleResponse(response);
    }

    async getPlayback(body: { continuingEducationVideoId: string }): Promise<CePlaybackResponse> {
        const response = await this.postJson('/v1/continuing-education/playback/get', body);
        return await this.handleResponse(response);
    }

    async adminGetPlayback(body: { continuingEducationVideoId: string }): Promise<CePlaybackResponse> {
        const response = await this.postJson('/v1/continuing-education-admin/playback/get', body);
        return await this.handleResponse(response);
    }

    async adminListVideos(): Promise<{ success?: boolean; videos?: CeAdminVideo[]; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/video/list', {});
        return await this.handleResponse(response);
    }

    async adminSaveVideo(body: Record<string, unknown>): Promise<{ success?: boolean; video?: CeAdminVideo; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/video/save', body);
        return await this.handleResponse(response);
    }

    async adminGetVideoViewStats(continuingEducationVideoId: string): Promise<{ success?: boolean; stats?: CeVideoViewStat[]; totalViews?: number; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/video/view-stats', { continuingEducationVideoId });
        return await this.handleResponse(response);
    }

    async adminReorderVideos(videos: { continuingEducationVideoId: string; sortOrder: number }[]): Promise<{ success?: boolean; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/video/reorder', { videos });
        return await this.handleResponse(response);
    }

    async adminDeleteVideo(continuingEducationVideoId: string): Promise<{ success?: boolean; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/video/delete', {
            continuingEducationVideoId,
        });
        return await this.handleResponse(response);
    }

    async adminCreateDirectUpload(body: {
        continuingEducationVideoId: string;
        maxDurationSeconds?: number;
    }): Promise<{ success?: boolean; uploadUrl?: string; cloudflareStreamVideoUid?: string; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/stream/direct-upload/create', body);
        return await this.handleResponse(response);
    }

    async adminRefreshStream(continuingEducationVideoId: string): Promise<{ success?: boolean; video?: CeAdminVideo; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/stream/refresh', {
            continuingEducationVideoId,
        });
        return await this.handleResponse(response);
    }

    async adminListEntitlements(body: {
        continuingEducationVideoId?: string | null;
        userId?: string | null;
    }): Promise<{ success?: boolean; entitlements?: unknown[]; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/entitlement/list', body);
        return await this.handleResponse(response);
    }

    async adminGrantEntitlement(userId: string, continuingEducationVideoId: string): Promise<{ success?: boolean; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/entitlement/grant', {
            userId,
            continuingEducationVideoId,
        });
        return await this.handleResponse(response);
    }

    async adminRevokeEntitlement(userContinuingEducationVideoId: string): Promise<{ success?: boolean; message?: string }> {
        const response = await this.postJson('/v1/continuing-education-admin/entitlement/revoke', { userContinuingEducationVideoId });
        return await this.handleResponse(response);
    }

    async adminGrantEntitlementBatch(body: {
        userIds: string[];
        continuingEducationVideoIds: string[];
    }): Promise<{
        success?: boolean;
        pairsProcessed?: number;
        userCount?: number;
        videoCount?: number;
        message?: string;
    }> {
        const response = await this.postJson('/v1/continuing-education-admin/entitlement/grant-batch', {
            userIds: body.userIds,
            continuingEducationVideoIds: body.continuingEducationVideoIds,
        });
        return await this.handleResponse(response);
    }
}

export const continuingEducationApiService = new ContinuingEducationApiService();
